import type {ImageReference} from '@github-ui/copilot-chat-types'
import type {DotcomFileAttachment} from '@github-ui/copilot-chat-types/uploadable-file-attachment'
import {ssrSafeWindow} from '@github-ui/ssr-utils'

import {ImageReferenceToken} from './ImageReferenceToken'

/**
 * Legacy attachment path pattern: /<org>/<repo>/assets/<id>/<guid>
 * The guid should be alphanumeric/hyphens without a file extension.
 */
const LEGACY_ASSET_PATH_PATTERN = /^\/[\w.-]+\/[\w.-]+\/assets\/\d+\/[^.]+$/

/**
 * Creates a minimal DotcomFileAttachment for URL-only image references.
 * Used when we detect an image URL in markdown that's already uploaded to GitHub,
 * so no actual upload is needed - we just wrap the existing URL.
 */
export function createUrlAttachment(url: string, name: string): DotcomFileAttachment {
  const dummyFile = new File([], name, {type: 'image/*'})
  return {
    key: url,
    file: dummyFile,
    previewUrl: url,
    url: async () => url,
    prefetch: async () => {},
    isLoaded: true,
    hasError: false,
    threadID: null,
    getDimensions: async () => ({width: undefined, height: undefined}),
  }
}

/**
 * Check if a URL is a GitHub asset image URL that sweagentd can resolve.
 * Matches both user attachments and legacy attachments.
 *
 * Supported patterns:
 * - User attachments: ${origin}/user-attachments/assets/...
 * - Legacy attachments: ${origin}/<org>/<repo>/assets/<id>/<guid>
 */
export function isGitHubAssetUrl(urlString: string): boolean {
  const origin = ssrSafeWindow?.location.origin ?? 'https://github.com'
  let url: URL
  try {
    url = new URL(urlString, origin)
  } catch {
    return false
  }

  // URL must be same-origin
  if (url.origin !== origin) {
    return false
  }

  // User attachments: /user-attachments/assets/...
  if (url.pathname.startsWith('/user-attachments/assets/')) {
    return true
  }

  // Legacy attachments: /<org>/<repo>/assets/<id>/<guid>
  return LEGACY_ASSET_PATH_PATTERN.test(url.pathname)
}

/**
 * Builds an ImageReference from a GitHub-asset image URL and its alt text.
 * Wraps the already-uploaded URL in a URL-only attachment, so no manager or
 * upload is involved.
 */
export function buildImageReference(url: string, alt?: string): ImageReference {
  const name = alt || 'Image'
  return {
    type: 'image',
    id: `image:${url}`,
    name,
    imageUrl: url,
    attachment: createUrlAttachment(url, name),
    TokenComponent: ImageReferenceToken,
  }
}
